import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import { Skeleton } from 'antd';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  InfoTileCard,
  InfoTileHeader,
  InfoTileContent,
  InfoTileTitle,
  InfoTileSubtitle,
  ChartContainer,
  ChartValue,
  ChartValueText,
} from './styles';
import { InfoTileProps } from './types';
import { CHART_OPTIONS } from './constants';

ChartJS.register(ArcElement, Tooltip, Legend);

const InfoTile: React.FC<InfoTileProps> = ({
  title,
  subTitle,
  value,
  icon,
  chartData,
  loading = false,
}) => {
  const chartDataConfig = chartData
    ? {
        labels: chartData.labels,
        datasets: [
          {
            data: chartData.data,
            backgroundColor: chartData.backgroundColor,
            hoverBackgroundColor: chartData.hoverBackgroundColor,
            borderWidth: 0,
          },
        ],
      }
    : null;

  const chartOptions = {
    ...CHART_OPTIONS,
    plugins: {
      ...CHART_OPTIONS.plugins,
      tooltip: {
        ...CHART_OPTIONS.plugins.tooltip,
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.formattedValue || '';
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = total > 0 ? ((context.raw / total) * 100).toFixed(2) : '0';
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
  };

  if (loading) {
    return (
      <InfoTileCard>
        <InfoTileHeader>
          <Skeleton.Avatar active size={32} shape="circle" />
          <InfoTileContent style={{ flex: 1, marginLeft: '12px' }}>
            <Skeleton active paragraph={{ rows: 1, width: '80%' }} title={false} />
            <Skeleton active paragraph={{ rows: 1, width: '60%' }} title={false} />
          </InfoTileContent>
        </InfoTileHeader>
        <ChartContainer>
          <Skeleton.Avatar active size={120} shape="circle" style={{ margin: '0 auto', display: 'block' }} />
        </ChartContainer>
      </InfoTileCard>
    );
  }

  return (
    <InfoTileCard>
      <InfoTileHeader>
        <FontAwesomeIcon icon={icon} size="2x" style={{ marginTop: '4px' }} />
        <InfoTileContent>
          <InfoTileTitle level={5}>{title}</InfoTileTitle>
          <InfoTileSubtitle>{subTitle}</InfoTileSubtitle>
        </InfoTileContent>
      </InfoTileHeader>
      {chartDataConfig && (
        <ChartContainer>
          <Doughnut data={chartDataConfig} options={chartOptions} />
          <ChartValue>
            <ChartValueText>{value}</ChartValueText>
          </ChartValue>
        </ChartContainer>
      )}
    </InfoTileCard>
  );
};

export default InfoTile;
